// ============================================================================
// KLINISOL CRO - Dashboard Component (WITH RBAC)
// ✅ ILO App, Knowledge Base, Training Portal, SOP Library
// ✅ SOP Upload, Master SOP Register, Form Portal, User Management
// ✅ Role-Based Access Control (RBAC)
// ✅ Change Password modal
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// ============================================================================

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users,           // User Management
  FileText,        // Training Portal
  Settings,        // SOP Library
  PowerOff,        // Logout
  Brain,           // ILO App
  Database,        // Knowledge Base
  Upload,          // SOP Upload
  List,            // Master SOP Register
  ClipboardList,   // Form Portal
  Grid3X3,         // Training Matrix
  Lock,            // Change Password
  UserCheck        // Personnel Qualifications
} from "lucide-react";
import { useAuth } from '../context/AuthContext';
import ChangePassword from './ChangePassword';

/**
 * KLINiSOL Dashboard — 8 Modules with RBAC
 * --------------------------------------------------------
 * Role-Based Access Control:
 * - User Management: Super Admin only
 * - SOP Upload: Super Admin + Admin only
 * - All other modules: Everyone (backend enforces write permissions)
 * 
 * Roles: Super Admin, Admin, QA, CRA, Investigator, Study Coordinator, Viewer
 */

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showChangePassword, setShowChangePassword] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  /**
   * Check if user has access to a module based on role
   */
  const hasAccess = (item) => {
    const userRole = user?.role || '';
    
    // Super Admin sees everything
    if (userRole === 'Super Admin') return true;
    
    // User Management - Super Admin only
    if (item.superAdminOnly) return false;
    
    // SOP Upload - Super Admin + Admin only
    if (item.adminOnly) {
      return ['Admin'].includes(userRole);
    }
    
    // Everything else - everyone can see
    return true;
  };

  /**
   * Get badge for module based on access level
   */
  const getAccessBadge = (item) => {
    if (item.superAdminOnly) {
      return { text: 'ADMIN', color: 'bg-red-500' };
    }
    if (item.adminOnly) {
      return { text: 'ADMIN', color: 'bg-amber-500' };
    }
    return null;
  };

  const modules = [
    {
      title: "ILO App",
      desc: "AI-powered clinical research intelligence platform.",
      icon: Brain,
      route: "/ilo",
      highlight: true,
    },
    {
      title: "Knowledge Base",
      desc: "Search and access organizational knowledge resources.",
      icon: Database,
      route: "/knowledge",
    },
    {
      title: "Training Portal",
      desc: "Access internal learning and compliance modules.",
      icon: FileText,
      route: "/training-portal",
    },
    {
      title: "Training Matrix",
      desc: "RACI-based training requirements and compliance tracking.",
      icon: Grid3X3,
      route: "/training-matrix",
    },
    {
      title: "SOP Library",
      desc: "View, download, and acknowledge the latest SOPs.",
      icon: Settings,
      route: "/sop-library",
    },
    {
      title: "SOP Upload",
      desc: "Upload and manage Standard Operating Procedures.",
      icon: Upload,
      route: "/sop-upload",
      adminOnly: true,
    },
    {
      title: "Master SOP Register",
      desc: "View comprehensive registry of all SOPs.",
      icon: List,
      route: "/master-sop-register",
    },
    {
      title: "Form Portal",
      desc: "Submit and manage regulatory forms and documents.",
      icon: ClipboardList,
      route: "/form-portal",
    },
    {
      title: "Personnel Qualifications",
      desc: "Staff directory, documents, and compliance tracking.",
      icon: UserCheck,
      route: "/personnel",
    },
    {
      title: "User Management",
      desc: "Manage user access, roles, and permissions.",
      icon: Users,
      route: "/user-management",
      superAdminOnly: true,
    },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafd] font-sans text-gray-800">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-8 px-6 shadow-md">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <h1 className="text-3xl font-bold tracking-tight">
              KLIN<span className="text-white">i</span>SOL
            </h1>
            <p className="text-sm opacity-90">
              Advancing Investigator Initiated Research
            </p>
          </div>

          {/* User Info & Actions */}
          {user && (
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-semibold">{`${user.firstName} ${user.lastName}`}</p>
                <p className="text-xs opacity-80 capitalize">{user.role || "User"}</p>
              </div>
              
              {/* Change Password Button */}
              <button
                onClick={() => setShowChangePassword(true)}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-all flex items-center justify-center group"
                title="Change Password"
              >
                <Lock className="w-4 h-4 text-white" />
              </button>
              
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="w-10 h-10 rounded-full bg-gradient-to-br from-[#475569] to-[#E91E63] hover:from-[#334155] hover:to-[#C2185B] transition-all shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center group"
                title="Logout"
              >
                <PowerOff className="w-4 h-4 text-white group-hover:rotate-12 transition-transform" />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Quick Access Modules */}
        <section className="bg-white border border-gray-100 rounded-2xl shadow-sm p-8 mb-12">
          <h2 className="text-2xl font-semibold text-[#475569] mb-6">
            Quick Access
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {modules
              .filter(item => hasAccess(item))
              .map(({ title, desc, icon: Icon, route, highlight, adminOnly, superAdminOnly }) => {
                const badge = getAccessBadge({ adminOnly, superAdminOnly });
                
                return (
                  <button
                    key={title}
                    onClick={() => navigate(route)}
                    className={`group bg-[#fdfdfd] border rounded-xl p-4 shadow-sm hover:shadow-md transition flex flex-col justify-between text-left ${
                      highlight 
                        ? 'border-[#E91E63] bg-gradient-to-br from-white to-pink-50' 
                        : 'border-gray-100 hover:border-[#E91E63]/50'
                    }`}
                  >
                    <div>
                      <Icon className={`w-10 h-10 mb-3 transition ${
                        highlight 
                          ? 'text-[#E91E63]' 
                          : 'text-[#475569] group-hover:text-[#E91E63]'
                      }`} />
                      <h3 className="text-lg font-semibold text-[#475569] mb-1">
                        {title}
                        {highlight && (
                          <span className="ml-2 text-xs bg-[#E91E63] text-white px-2 py-0.5 rounded-full">
                            NEW
                          </span>
                        )}
                        {badge && (
                          <span className={`ml-2 text-xs ${badge.color} text-white px-2 py-0.5 rounded-full`}>
                            {badge.text}
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-600">{desc}</p>
                    </div>
                    <div className="mt-4 text-sm text-[#E91E63] font-medium opacity-0 group-hover:opacity-100 transition">
                      Open →
                    </div>
                  </button>
                );
              })}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-6 px-6 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <h3 className="text-xl font-bold mb-2">
            KLINiSOL
          </h3>
          <p className="text-sm opacity-90">© 2025 KLINiSOL CRO. All rights reserved.</p>
        </div>
      </footer>

      {/* Change Password Modal */}
      <ChangePassword 
        isOpen={showChangePassword} 
        onClose={() => setShowChangePassword(false)} 
      />
    </div>
  );
}
